import React, { createContext, useState, useContext, ReactNode, Dispatch, SetStateAction, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { User } from '../types/Types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL ?? '';
const API_URL = `${API_BASE}/api`;

const DEFAULT_INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const DEFAULT_WARNING_BEFORE_MS = 10 * 1000;          // show warning 10 seconds before logout


interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (userData: User, authToken: string) => void;
  logout: () => void;
  setUser: Dispatch<SetStateAction<User | null>>;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  login: () => { },
  logout: () => { },
  setUser: () => { },
  refreshUser: async () => { },
  isLoading: true,
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Hydrate the cached session synchronously so protected screens do not show
  // an auth spinner first and then their own data spinner on browser refresh.
  // The mount effect below still revalidates this user against /users/me.
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return null;
    try {
      return JSON.parse(storedUser) as User;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [inactivityTimeoutMs, setInactivityTimeoutMs] = useState<number>(DEFAULT_INACTIVITY_TIMEOUT_MS);
  const [warningBeforeMs, setWarningBeforeMs] = useState<number>(DEFAULT_WARNING_BEFORE_MS);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');

    // Serve stored user immediately so the spinner shows the correct logged-in state
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    if (!storedToken) {
      // No token — not logged in
      setLoading(false);
      return;
    }

    // Re-validate session and fetch fresh permissions before route guards fire.
    // Keeps isLoading=true until complete so ProtectedRoute never flashes stale access.
    // 15-second timeout: on ECONNABORTED the catch below keeps the cached user (no 401
    // branch fires) and finally() sets loading=false, so the app stays usable offline.
    axios.get(`${API_URL}/users/me`, {
      headers: { 'x-auth-token': storedToken },
      timeout: 15000,
    }).then((response) => {
      if (response.data) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    }).catch((err) => {
      if (err.response?.status === 401) {
        // Token invalid or revoked — clear session (fail-closed)
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
      // Other errors (network issues): keep stored user, app uses cached perms
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const login = (userData: User, authToken: string) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = useCallback(() => {
    // Tell the server first so every token of this user dies (odjava насекаде); the local
    // cleanup never waits on it — an expired/invalid token just fails silently.
    const current = localStorage.getItem('token');
    if (current) {
      axios.post(`${API_URL}/logout`, null, {
        headers: { 'x-auth-token': current },
        skipInactivityReset: true,
      } as any).catch(() => {});
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    setShowWarning(false);
    navigate('/');
  }, [navigate]);

  const clearAllTimers = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);

    // inactivityTimeoutMs === 0 means auto-logout is disabled — no warning
    // modal, no timers. This is called on every axios request via the
    // interceptor below, so it must stay a cheap no-op when disabled.
    if (inactivityTimeoutMs === 0) return;

    setCountdown(10);

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(10);

      let remaining = 10;
      countdownIntervalRef.current = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0) {
          if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        }
      }, 1000);
    }, Math.max(0, inactivityTimeoutMs - warningBeforeMs));

    logoutTimerRef.current = setTimeout(() => {
      logout();
    }, inactivityTimeoutMs);
  }, [clearAllTimers, logout, inactivityTimeoutMs, warningBeforeMs]);

  // Load security settings (inactivity timeout + warning) when authenticated
  useEffect(() => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;

    const load = async () => {
      try {
        const response = await axios.get(`${API_URL}/security-settings`, {
          headers: { 'x-auth-token': currentToken }
        });
        const s = response.data || {};
        const timeoutMin = Number(s.inactivityTimeoutMinutes);
        const warnSec = Number(s.inactivityWarningSeconds);
        // timeoutMin === 0 means "inactivity auto-logout disabled" — allow it
        // through (>= 0). Missing/invalid values fall through and keep the
        // 10-minute default untouched, same as before.
        if (Number.isFinite(timeoutMin) && timeoutMin >= 0) {
          setInactivityTimeoutMs(timeoutMin * 60 * 1000);
        }
        if (Number.isFinite(warnSec) && warnSec >= 0) {
          setWarningBeforeMs(warnSec * 1000);
          setCountdown(Math.max(1, Math.floor(warnSec)));
        }
      } catch {
        // keep defaults
      }
    };
    load();
  }, [token]);

  // Start/stop inactivity tracking based on whether user is logged in
  useEffect(() => {
    if (!user) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    // Auto-logout disabled — clear any existing timers and skip registering
    // activity listeners entirely.
    if (inactivityTimeoutMs === 0) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    activityEvents.forEach(event => window.addEventListener(event, handleActivity));
    resetInactivityTimer();

    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, handleActivity));
      clearAllTimers();
    };
  }, [user, resetInactivityTimer, clearAllTimers, inactivityTimeoutMs]);

  const handleStayLoggedIn = () => {
    resetInactivityTimer();
  };

  const refreshUser = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;

    try {
      const response = await axios.get(`${API_URL}/users/me`, {
        headers: { 'x-auth-token': currentToken }
      });

      if (response.data) {
        const userData = response.data;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (err) {
      console.error('Error refreshing user:', err);
    }
  }, []);

  // Silently get a new token from the server (called before expiry)
  const silentRefreshToken = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;
    try {
      const response = await axios.post(`${API_URL}/refresh-token`, {}, {
        headers: { 'x-auth-token': currentToken }
      });
      const newToken = response.data?.token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        setToken(newToken);
      }
    } catch {
      // Token truly expired or invalid — logout
      logout();
    }
  }, [logout]);

  // Proactively refresh token every 20 hours (token lasts 24h)
  useEffect(() => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken || !user) return;
    const interval = setInterval(() => {
      silentRefreshToken();
    }, 20 * 60 * 60 * 1000); // 20 hours
    return () => clearInterval(interval);
  }, [user, silentRefreshToken]);

  // Global axios interceptors: reset inactivity timer on every request,
  // handle 401 responses by refreshing token once before giving up
  const resetTimerRef = useRef(resetInactivityTimer);
  useEffect(() => { resetTimerRef.current = resetInactivityTimer; }, [resetInactivityTimer]);
  const logoutRef = useRef(logout);
  useEffect(() => { logoutRef.current = logout; }, [logout]);
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      // Any API activity resets the inactivity countdown — except background polling
      // (unread-count every 30 s), which would keep an abandoned tab logged in forever.
      if (userRef.current && !(config as any).skipInactivityReset) resetTimerRef.current();
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && userRef.current && !isRefreshingRef.current) {
          // Attempt a silent token refresh once before logging out
          isRefreshingRef.current = true;
          const currentToken = localStorage.getItem('token');
          if (currentToken) {
            try {
              const refreshResp = await axios.post(`${API_URL}/refresh-token`, {}, {
                headers: { 'x-auth-token': currentToken }
              });
              const newToken = refreshResp.data?.token;
              if (newToken) {
                localStorage.setItem('token', newToken);
                setToken(newToken);
                isRefreshingRef.current = false;
                // Retry the original request with the new token
                error.config.headers['x-auth-token'] = newToken;
                return axios(error.config);
              }
            } catch {
              // Refresh also failed — token is truly invalid, logout
              logoutRef.current();
            }
          } else {
            logoutRef.current();
          }
          isRefreshingRef.current = false;
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []); // set up once

  return (
    <AuthContext.Provider value={{ user, token, login, logout, setUser, refreshUser, isLoading }}>
      {children}

      {showWarning && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999] backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl shadow-2xl w-96 border border-gray-200">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t('sessionTimeout.title')}
              </h2>
              <p className="text-gray-600 text-sm">
                {t('sessionTimeout.message')}{' '}
                <span className="font-bold text-amber-600 text-lg">{countdown}</span>{' '}
                {t('sessionTimeout.seconds')}
              </p>
            </div>
            <button
              onClick={handleStayLoggedIn}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              {t('sessionTimeout.stayLoggedIn')}
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
